import { Dispatch, SetStateAction, useContext, useMemo } from 'react';

import { Checkbox, Label } from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { CellProps, Column } from 'react-table';

import { AuthContext } from 'auth';
import { ClientsAvatar } from 'components';
import { LocalizationContext } from 'localization';
import s from 'pages/Clients/Clients.module.scss';
import { TClient } from 'pages/Clients/interfaces';
import { SettingsContext } from 'settings';

export const useColumns: (
  UUIDs: string[],
  setSelectedClientsUUIDs: Dispatch<SetStateAction<string[]>>
) => Column<TClient>[] = (UUIDs, setSelectedClientsUUIDs) => {
  const { t } = useContext(LocalizationContext);

  const {
    state: {
      settings: {
        currency: { sign: currency },
      },
    },
  } = useContext(SettingsContext);
  const {
    data: { me },
  } = useContext(AuthContext);

  const isOperator = me?.roles.some((role) => role.alias === 'operator');

  const otherColumns: Column<TClient>[] = useMemo(
    () => [
      {
        Header: t('client.nickname'),
        accessor: ({ nickname, avatar_url, roles }) => ({
          nickname,
          avatar_url,
          roles,
        }),
        Cell: ({
          value: { nickname, avatar_url, roles },
        }: {
          value: {
            nickname: string;
            avatar_url: string;
            roles:
              | Array<{
                  alias: string;
                }>
              | [];
          };
        }) => {
          return (
            <div className={s.nickname}>
              <ClientsAvatar avatar={avatar_url} roles={roles} size="s" />
              <span>{nickname}</span>
            </div>
          );
        },
      },
      {
        Header: t('phone'),
        accessor: 'phone',
        disableSortBy: true,
      },
      {
        Header: t('client.lastVisit'),

        accessor: 'last_client_activity',
        Cell: ({ value }) => {
          const date = dayjs(value);
          const yearFormat = date.isSame(dayjs(), 'year') ? '' : ' YYYY';
          return (
            <>
              {date.format(`D MMM${yearFormat}`)}{' '}
              <span className={s.secondary}>{date.format('HH:mm')}</span>
            </>
          );
        },
      },
      {
        Header: t('discount'),
        id: 'user_discount',
        accessor: 'user_discount',
        Cell: ({ value }) => <Label text={`${value}%`} color="blue" rounded />,
        width: 107,
      },
      {
        id: 'user_group',
        Header: t('group'),
        accessor: ({ group }) =>
          group ? `${group.title} ${group.discount}%` : '—',
        Cell: ({ value }: CellProps<TClient>) => (
          <Label text={value} color="gray-primary" rounded />
        ),
        width: 229,
      },
      {
        Header: t('money.deposit'),
        id: 'deposit',
        accessor: ({ deposit, bonus }) => ({ deposit, bonus }),
        Cell: ({
          value: { deposit, bonus },
        }: {
          value: { deposit: number; bonus: number };
        }) => {
          return (
            <div className={s.balance}>
              <Label
                iconName="WalletIcon"
                text={`${deposit.toLocaleString()} ${currency}`}
                color="violetGradient"
                rounded
              />
              <Label iconName="BonusIcon" text={String(bonus)} color="violet" />
            </div>
          );
        },
        width: 248,
      },
      {
        id: 'banned_at',
        Header: t('status'),
        accessor: ({ banned_at, unverified }) => {
          if (banned_at) {
            return 'banned';
          }
          if (unverified) {
            return 'unverified';
          }
          return 'active';
        },
        Cell: ({ value: status }: CellProps<TClient>) => {
          switch (status) {
            case 'banned':
              return (
                <Label
                  iconName="LockIcon"
                  text={t('client.status.banned')}
                  color="red"
                  rounded
                />
              );
            case 'unverified':
              return (
                <Label
                  iconName="InfoIcon"
                  text={t('client.status.unverified')}
                  color="orange"
                  rounded
                />
              );
            case 'active':
            default:
              return (
                <Label
                  iconName="SuccessIcon"
                  text={t('client.status.active')}
                  color="green"
                  rounded
                />
              );
          }
        },
      },

      {
        Header: t('registration'),
        accessor: 'created_at',
        Cell: ({ value }) => (
          <span className={s.secondary}>
            {dayjs(value).format('D MMM YYYY HH:mm')}
          </span>
        ),
      },
    ],
    [currency, t]
  );

  const selectedColumn: Column<TClient>[] = useMemo(
    () => [
      {
        id: 'selected',
        Header: ({ isAllRowsSelected, toggleAllRowsSelected, rows }) => (
          <Checkbox
            value={isAllRowsSelected}
            onChange={() => {
              setSelectedClientsUUIDs(
                isAllRowsSelected ? [] : rows.map((row) => row.original.uuid)
              );
              toggleAllRowsSelected();
            }}
          />
        ),
        accessor: () => true,
        Cell: ({ row }: CellProps<TClient>) => (
          <Checkbox value={Boolean(row.isSelected)} onChange={() => void 0} />
        ),
        width: 70,
        disableSortBy: true,
      },
    ],
    []
  );

  return useMemo(() => {
    if (isOperator) {
      return otherColumns;
    }
    return selectedColumn.concat(otherColumns);
  }, [isOperator, otherColumns, selectedColumn]);
};
